import type { IDBPDatabase, DBSchema } from 'idb';
import { openDB } from 'idb';

import { type Schema as PMSchema } from '@atlaskit/editor-prosemirror/model';
import type { StepJson } from '@atlaskit/editor-common/collab';
import { Step } from '@atlaskit/editor-prosemirror/transform';

const OFFLINE_DB_STORE_NAME = 'steps';

interface StepsDBSchema extends DBSchema {
	steps: {
		key: number;
		value: {
			step: StepJson;
		};
	};
}

export class StepsDatabase {
	private dbPromise: Promise<IDBPDatabase<StepsDBSchema>> | undefined;
	private _contentId: string | undefined;

	async initDB(contentId: string): Promise<IDBPDatabase<StepsDBSchema> | undefined> {
		if (window?.indexedDB === undefined) {
			return undefined;
		}
		if (this._contentId === contentId && this.dbPromise) {
			return this.dbPromise;
		}
		if (this.dbPromise) {
			const db = await this.dbPromise;
			db.close();
		}

		const dbName = `editor:offline-steps-${contentId}`;
		this.dbPromise = openDB<StepsDBSchema>(dbName, 1, {
			upgrade(db) {
				if (!db.objectStoreNames.contains(OFFLINE_DB_STORE_NAME)) {
					db.createObjectStore(OFFLINE_DB_STORE_NAME, { keyPath: 'id', autoIncrement: true });
				}
			},
			terminated: () => {
				// Currently have no mechanism to handle recovery if DB connection is terminated
				this.dbPromise = undefined;
			},
		});
		this._contentId = contentId;
		return this.dbPromise;
	}

	get hasOpenDBConnection() {
		return this.dbPromise !== undefined;
	}

	async updateSteps(steps: readonly Step[]) {
		if (!this.dbPromise) {
			return;
		}

		const db = await this.dbPromise;
		const tx = db.transaction(OFFLINE_DB_STORE_NAME, 'readwrite');
		const offlineDB = tx.objectStore(OFFLINE_DB_STORE_NAME);

		// Clear existing steps
		await offlineDB.clear();
		// Serialize each step to Json
		const serializedSteps = steps.map((step) => step.toJSON());
		// Store serialized steps in the database
		for (const serializedStep of serializedSteps) {
			await offlineDB.add({ step: serializedStep });
		}
		await tx.done;
	}

	async getSteps(pmSchema: PMSchema): Promise<Step[]> {
		if (!this.dbPromise) {
			throw new Error('StepsDatabase not initialized');
		}

		const db = await this.dbPromise;
		const tx = db.transaction(OFFLINE_DB_STORE_NAME, 'readonly');
		const store = tx.objectStore(OFFLINE_DB_STORE_NAME);

		const storedSteps: Step[] = [];
		let cursor = await store.openCursor();

		while (cursor) {
			const stepJson = cursor.value.step;
			const step = Step.fromJSON(pmSchema, stepJson);
			storedSteps.push(step);
			cursor = await cursor.continue();
		}

		await tx.done;
		return storedSteps;
	}

	async clear() {
		if (!this.dbPromise) {
			return;
		}

		const db = await this.dbPromise;
		const tx = db.transaction(OFFLINE_DB_STORE_NAME, 'readwrite');
		const store = tx.objectStore(OFFLINE_DB_STORE_NAME);
		await store.clear();
		await tx.done;
	}
}
